import type { Container, Sprite } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { GAME } from '@/config/game';
import type { BulletSystem } from '@/entities/bullets';
import { setCircle, type BossEvents, type HitCircle } from './boss-hooks';

/** Corner turret tuning (docs/02 B2/B4). */
export const CT = {
  count: 4,
  /** Distance from the arena edge (past the wall band) */
  inset: 170,
  radius: 24,
  /** Placement: kept clear of rocks by this pad and this far from the ship */
  clearPad: 16,
  clearAway: 220,
  /** Each turret's HP as a share of the boss max HP — optional targets */
  hpShare: 0.05,
  unfold: 0.6,
  /** Seconds between shots at tempo 1, staggered per turret */
  fireInterval: 3.2,
  stagger: 0.8,
  /** Lens glows this long before each shot (in-fiction telegraph) */
  charge: 0.45,
  shotSpeed: 240,
  shotSpread: 0.09,
  shotRadius: 10,
} as const;

interface Turret {
  alive: boolean;
  hp: number;
  x: number;
  y: number;
  fire: number;
  sprite: Sprite;
}

/**
 * The Carrier's phase-2 corner turrets: crystal pylons that unfold in the
 * arena corners and snipe paired shards. Separate HP (they do NOT drain the
 * boss bar) — killing them just makes the phase easier.
 */
export class CarrierTurrets {
  private readonly list: Turret[] = [];
  private grow = 0;
  deployed = false;

  constructor(view: Container) {
    for (let i = 0; i < CT.count; i++) {
      const sprite = makeSprite('boss/carrier/turret', 0);
      sprite.visible = false;
      view.addChild(sprite);
      this.list.push({ alive: false, hp: 0, x: 0, y: 0, fire: 0, sprite });
    }
  }

  /**
   * Unfold in the corners. `events.clearPoint` (the arena) nudges a corner
   * that sits on a rock to the nearest clear spot, away from the ship.
   */
  deploy(
    arenaW: number, arenaH: number, hp: number, events: BossEvents | null, px: number, py: number,
  ): void {
    const inset = GAME.arena.wallThickness + CT.inset;
    this.deployed = true;
    this.list.forEach((t, i) => {
      t.alive = true;
      t.hp = hp;
      const cx = i % 2 === 0 ? inset : arenaW - inset;
      const cy = i < 2 ? inset : arenaH - inset;
      const p = events?.clearPoint?.(
        cx / arenaW, cy / arenaH, px, py, CT.clearAway, CT.radius + CT.clearPad,
      );
      t.x = p?.x ?? cx;
      t.y = p?.y ?? cy;
      t.fire = CT.unfold + CT.fireInterval * 0.5 + i * CT.stagger;
      t.sprite.visible = true;
    });
  }

  get alive(): number {
    let n = 0;
    for (const t of this.list) if (t.alive) n++;
    return n;
  }

  update(
    dt: number, px: number, py: number, bullets: BulletSystem, tempo: number, shot: number,
  ): void {
    if (!this.deployed) return;
    this.grow = Math.min(1, this.grow + dt / CT.unfold);
    for (const t of this.list) {
      if (!t.alive) continue;
      t.fire -= dt * tempo;
      if (t.fire > 0) continue;
      t.fire = CT.fireInterval;
      const a = Math.atan2(py - t.y, px - t.x);
      for (let k = -1; k <= 1; k += 2) {
        bullets.spawn({
          x: t.x, y: t.y, dirX: Math.cos(a + k * CT.shotSpread), dirY: Math.sin(a + k * CT.shotSpread),
          speed: CT.shotSpeed * shot, life: 5, radius: CT.shotRadius, damage: 1,
          friendly: false, texKey: 'bullet/enemy/shard', rotates: false,
        });
      }
    }
  }

  /** Player shots vs turrets. @returns damage dealt */
  absorb(bullets: BulletSystem, onKill: (x: number, y: number) => void): number {
    let dealt = 0;
    for (const t of this.list) {
      if (!t.alive) continue;
      bullets.collideCircle(true, t.x, t.y, CT.radius, (dmg) => {
        const taken = this.hurt(t, dmg, onKill);
        dealt += taken;
        return taken; // the turret's own loss (never the boss bar)
      });
    }
    return dealt;
  }

  /** Blast vs the turret inside it; `at` gets its hit circle. @returns damage dealt */
  applyAoe(
    x: number, y: number, radius: number, damage: number,
    onKill: (x: number, y: number) => void, at: HitCircle,
  ): number {
    const t = this.closest(x, y, radius + CT.radius);
    if (!t) return 0;
    setCircle(at, t.x, t.y, CT.radius);
    return this.hurt(t, damage, onKill);
  }

  private hurt(t: Turret, dmg: number, onKill: (x: number, y: number) => void): number {
    if (!t.alive) return 0;
    const dealt = Math.min(t.hp, dmg);
    t.hp -= dmg;
    if (t.hp <= 0) {
      t.alive = false;
      t.sprite.visible = false;
      onKill(t.x, t.y);
    }
    return dealt;
  }

  /** Nearest living turret within `reach` (auto-aim while the crystal is shut). */
  nearest(x: number, y: number, reach = Infinity): { x: number; y: number } | null {
    return this.closest(x, y, reach);
  }

  private closest(x: number, y: number, reach: number): Turret | null {
    let best: Turret | null = null;
    let bestD = reach;
    for (const t of this.list) {
      if (!t.alive) continue;
      const d = Math.hypot(t.x - x, t.y - y);
      if (d < bestD) {
        bestD = d;
        best = t;
      }
    }
    return best;
  }

  /** Sprites live in the boss view → draw relative to its position. */
  render(viewX: number, viewY: number, px: number, py: number, tempo: number): void {
    for (const t of this.list) {
      if (!t.alive) continue;
      t.sprite.position.set(t.x - viewX, t.y - viewY);
      t.sprite.rotation = Math.atan2(py - t.y, px - t.x);
      const charging = t.fire / tempo < CT.charge;
      t.sprite.texture = getTexture('boss/carrier/turret', charging ? 1 : 0);
      t.sprite.scale.set(this.grow * (charging ? 1.1 : 1));
    }
  }
}
